'use client';

import type { ReactNode } from 'react';
import type {
  LeisureSearchRequestBody,
  LeisureSearchHeaderResponse,
  TabInfo,
  LeisureCategoryResponse,
} from '@weberus/shared-types';
import type { GlobalFilter } from '../store/search-filter';
import { useHydrateAtoms } from 'jotai/utils';

// 페이지 전용 Jotai 상태
import {
  baseCategoryAtom,
  largeCategoryAtom,
  leisureListReqBodyAtom,
  mediumCategoryAtom,
  pageNameAtom,
} from '../store/leisure-search';
import {
  globalFilterAtom,
  globalFilterParamsAtom,
  searchFilterKeywordAtom,
  originalGlobalFilterAtom,
  quickFilterAtom,
  searchTypeAtom,
  searchFilterSortAtom,
  sortInfoAtom,
  tabInfoAtom,
  sessionKeyAtom,
  searchTypeKeyAtom,
} from '../store/search-filter';

interface JotaiProviderProps {
  children: ReactNode;
  keyword?: string; // SRP only
  sort?: string;
  searchHeader: LeisureSearchHeaderResponse;
  pageName: 'KEYWORD' | 'CATEGORY';
  filters: GlobalFilter[] | undefined;
  searchType: string | undefined;
  searchTypeKey: 'searchType' | 'shortcut';
  largeCategory?: number; // SRP only
  mediumCategory?: number; // SRP only
  category?: LeisureCategoryResponse;
  listRequestBody: LeisureSearchRequestBody;
  tabInfo?: TabInfo['items'];
  sessionKey: string;
}

export function LeisureSearchProvider({
  children,
  keyword,
  sort,
  searchHeader,
  pageName,
  filters,
  searchType,
  searchTypeKey,
  largeCategory,
  mediumCategory,
  category,
  listRequestBody,
  tabInfo,
  sessionKey,
}: JotaiProviderProps) {
  // category 관련 초기 상태 계산
  const initialMediumCategory = category?.find((item) => item.id === mediumCategory);
  const initialSmallCategory = category?.find((item) => item.id.toString() === searchType);

  useHydrateAtoms([
    [pageNameAtom, pageName],
    [searchFilterKeywordAtom, keyword || ''],
    [searchFilterSortAtom, sort],
    [globalFilterParamsAtom, filters],
    [leisureListReqBodyAtom, listRequestBody],
    [searchTypeAtom, searchType],
    [searchTypeKeyAtom, searchTypeKey],
    [tabInfoAtom, tabInfo],
    [largeCategoryAtom, largeCategory],
    [mediumCategoryAtom, mediumCategory],
    // baseCategoryAtom 초기화 추가
    [
      baseCategoryAtom,
      {
        mediumCategory: initialMediumCategory,
        smallCategory: initialSmallCategory,
      },
    ],
    [
      quickFilterAtom,
      {
        globalFilter: searchHeader.globalFilter,
        quickFilter: searchHeader.quickFilter,
        hasSelectedFilter: !!searchHeader.hasSelectedFilter,
        includeFilterBtn: pageName === 'KEYWORD',
      },
    ],
    [sortInfoAtom, searchHeader.sortInfo],
    [originalGlobalFilterAtom, searchHeader.globalFilter],
    [globalFilterAtom, searchHeader.globalFilter],
    [sessionKeyAtom, sessionKey],
  ]);

  return <>{children}</>;
}
